<template>
  <b-container
    fluid
    class="px-0"
  >
    <b-row
      class="px-0"
      align-v="end"
    >
      <b-col
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.general.type') }}</label>
        <s-input-label-edit
          :formatted-value="getLabel(transformerInfo.type, typeOptions)"
          :editable="editMode"
          v-model="transformerInfo.type"
        >
          <s-select
            v-model="transformerInfo.type"
            :disabled="saving"
            :options="typeOptions"
            @input="onChangeType"
          />
        </s-input-label-edit>
      </b-col>
      <b-col
        lg="6"
        xl="4"
        cols="12"
      >
      <label>
        {{ $t('fields.asset.transformer.nominal-potency') }}
        <span class="pr-2">
          <s-icon
            name="hint"
            v-b-tooltip.hover.right
            :title="$t('asset-information.asset-info.engineering-modules-information')"
          />
        </span>
      </label>
      <s-input-label-edit
        v-model="transformerInfo.nominalPotency"
        :formatted-value="formatNumberValue(transformerInfo.nominalPotency, 2)"
        :editable="editMode"
      >
        <s-number-input
          class="text-right"
          v-model="transformerInfo.nominalPotency"
          :validation="$v.transformerInfo.nominalPotency"
          :invalid="$v.transformerInfo.nominalPotency.$invalid"
          :numeric-options="configureNumericOptionsPrecision(2)"
        />
        <b-form-invalid-feedback>
          {{ getRangeValueMessage('nominalPotency') }}
        </b-form-invalid-feedback>
      </s-input-label-edit>
      </b-col>
      <b-col
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.transformer-application') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          :editable="editMode"
          v-model="transformerInfo.transformerApplication"
          :formatted-value="getLabel(
            transformerInfo.transformerApplication,
            transformerApplicationOptions,
          )"
        >
          <s-select
            v-model="transformerInfo.transformerApplication"
            :disabled="saving"
            :state="$v.transformerInfo.transformerApplication.$invalid"
            :options="transformerApplicationOptions"
          />
          <b-form-invalid-feedback>
            {{ getRequiredValueMessage('fields.asset.transformer.transformer-application') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.higher-voltage') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          v-model="transformerInfo.superiorTension"
          :formatted-value="formatNumberValue(transformerInfo.superiorTension, 2)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.superiorTension"
            :validation="$v.transformerInfo.superiorTension"
            :invalid="$v.transformerInfo.superiorTension.$invalid"
            :numeric-options="configureNumericOptionsPrecision(2)"
          />
          <b-form-invalid-feedback>
            {{ getSuperiorTensionValidation('superiorTension') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.lower-voltage') }}</label>
        <s-input-label-edit
          v-model="transformerInfo.inferiorTension"
          :formatted-value="formatNumberValue(transformerInfo.inferiorTension, 2)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.inferiorTension"
            :validation="$v.transformerInfo.inferiorTension"
            :invalid="$v.transformerInfo.inferiorTension.$invalid"
            :numeric-options="configureNumericOptionsPrecision(2)"
            :disabled="options.inferiorTension.disabled"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('inferiorTension') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.tertiary-voltage') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          v-model="transformerInfo.tertiaryTension"
          :formatted-value="formatNumberValue(transformerInfo.tertiaryTension, 2)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.tertiaryTension"
            :validation="$v.transformerInfo.tertiaryTension"
            :invalid="$v.transformerInfo.tertiaryTension.$invalid"
            :numeric-options="configureNumericOptionsPrecision(2)"
            :disabled="options.tertiaryTension.disabled"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('tertiaryTension') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.nominal-chain-higher') }}</label>
        <s-input-label-edit
          v-model="transformerInfo.superiorNominalChain"
          :formatted-value="formatNumberValue(transformerInfo.superiorNominalChain, 2)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.superiorNominalChain"
            :validation="$v.transformerInfo.superiorNominalChain"
            :invalid="$v.transformerInfo.superiorNominalChain.$invalid"
            :numeric-options="configureNumericOptionsPrecision(2)"
          />
          <b-form-invalid-feedback>
            {{ getSuperiorNominalChainValidation('superiorNominalChain') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.nominal-chain-lower') }}</label>
        <s-input-label-edit
          v-model="transformerInfo.inferiorNominalChain"
          :formatted-value="formatNumberValue(transformerInfo.inferiorNominalChain, 2)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.inferiorNominalChain"
            :validation="$v.transformerInfo.inferiorNominalChain"
            :invalid="$v.transformerInfo.inferiorNominalChain.$invalid"
            :numeric-options="configureNumericOptionsPrecision(2)"
            :disabled="options.inferiorNominalChain.disabled"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('inferiorNominalChain') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.nominal-chain-tertiary') }}</label>
        <s-input-label-edit
          v-model="transformerInfo.tertiaryNominalChain"
          :formatted-value="formatNumberValue(transformerInfo.tertiaryNominalChain, 2)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.tertiaryNominalChain"
            :validation="$v.transformerInfo.tertiaryNominalChain"
            :invalid="$v.transformerInfo.tertiaryNominalChain.$invalid"
            :numeric-options="configureNumericOptionsPrecision(2)"
            :disabled="options.tertiaryNominalChain.disabled"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('tertiaryNominalChain') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.has-windings') }}</label>
        <s-input-label-edit
          :editable="editMode"
          v-model="transformerInfo.tertiaryWinding"
          :formatted-value="getLabel(
            transformerInfo.tertiaryWinding,
            windingCountOptions,
          )"
        >
          <s-select
            v-model="transformerInfo.tertiaryWinding"
            :options="windingCountOptions"
            :disabled="transformerIsReactorOrGrounding"
            @input="tertiaryWindingChange"
          />
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.number-of-monitored-windings') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          :editable="editMode"
          v-model="transformerInfo.monitoredWindingAmount"
          :formatted-value="transformerInfo.monitoredWindingAmount != null ? $n(getLabel(
            transformerInfo.monitoredWindingAmount,
            monitoredWindingOptions,
          )) : null"
        >
          <s-select
            v-model="transformerInfo.monitoredWindingAmount"
            :disabled="saving"
            :options="monitoredWindingOptions"
            :state="$v.transformerInfo.monitoredWindingAmount.$invalid"
            @input="monitoredWindingAmountChange"
          />
          <b-form-invalid-feedback>
            {{ getRequiredValueMessage('fields.asset.transformer.number-of-monitored-windings') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.voltage-regulation') }}</label>
        <s-input-label-edit
          :editable="editMode"
          v-model="transformerInfo.tensionRegulation"
          :formatted-value="getLabel(
            transformerInfo.tensionRegulation,
            tensionRegulationOptions,
          )"
        >
          <s-select
            v-model="transformerInfo.tensionRegulation"
            :disabled="saving"
            :options="tensionRegulationOptions"
            @change="onChangeTensionRegulation"
          />
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.oil-volume') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          v-model="transformerInfo.oilVolume"
          :formatted-value="formatNumberValue(transformerInfo.oilVolume, 1)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.oilVolume"
            :validation="$v.transformerInfo.oilVolume"
            :invalid="$v.transformerInfo.oilVolume.$invalid"
            :numeric-options="configureNumericOptionsPrecision(1)"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('oilVolume', 1) }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.oil-mass') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          v-model="transformerInfo.oilKiloMass"
          :editable="editMode"
          :formatted-value="formatNumberValue(transformerInfo.oilKiloMass, 1)">
          <s-number-input
            class="text-right"
            v-model="transformerInfo.oilKiloMass"
            :validation="$v.transformerInfo.oilKiloMass"
            :invalid="$v.transformerInfo.oilKiloMass.$invalid"
            :numeric-options="configureNumericOptionsPrecision(1)"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('oilKiloMass', 1) }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.tank-mass-and-accessories') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          v-model="transformerInfo.reservoirMass"
          :formatted-value="formatNumberValue(transformerInfo.reservoirMass, 1)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.reservoirMass"
            :validation="$v.transformerInfo.reservoirMass"
            :invalid="$v.transformerInfo.reservoirMass.$invalid"
            :numeric-options="configureNumericOptionsPrecision(1)"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('reservoirMass', 1) }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.mass-active-part') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          v-model="transformerInfo.activeSideMass"
          :formatted-value="formatNumberValue(transformerInfo.activeSideMass, 1)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.activeSideMass"
            :validation="$v.transformerInfo.activeSideMass"
            :invalid="$v.transformerInfo.activeSideMass.$invalid"
            :numeric-options="configureNumericOptionsPrecision(1)"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('activeSideMass', 1) }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.total-weight') }}</label>
        <s-input-label-edit
          v-model="transformerInfo.totalWeight"
          :formatted-value="formatNumberValue(transformerInfo.totalWeight, 1)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.totalWeight"
            :validation="$v.transformerInfo.totalWeight"
            :invalid="$v.transformerInfo.totalWeight.$invalid"
            :numeric-options="configureNumericOptionsPrecision(1)"
          />
          <b-form-invalid-feedback>
            {{ getTotalWeightValidation('totalWeight') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.temperature-elevation-class') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          :editable="editMode"
          v-model="transformerInfo.elevationClass"
          :formatted-value="getLabel(
            transformerInfo.elevationClass,
            elevationClassOptions,
          )"
        >
          <s-select
            v-model="transformerInfo.elevationClass"
            :disabled="saving"
            :options="elevationClassOptions"
            @change="elevationClassChanged"
          />
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.type-of-insulating-paper') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          :editable="editMode"
          v-model="transformerInfo.insulationType"
          :formatted-value="getLabel(
            transformerInfo.insulationType,
            insulatingPaperTypeOptions,
          )"
        >
          <s-select
            v-model="transformerInfo.insulationType"
            :options="insulatingPaperTypeOptions"
            :disabled="options.insulationType.disabled"
          />
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.oil-type') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          :editable="editMode"
          v-model="transformerInfo.oilType"
          :formatted-value="getLabel(
            transformerInfo.oilType,
            oilTypeOptions,
          )"
        >
          <s-select
            v-model="transformerInfo.oilType"
            :disabled="saving"
            :options="oilTypeOptions"
          />
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="6"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.communicating-oil') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          :editable="editMode"
          v-model="transformerInfo.communicatingOilOltcTransformer"
          :formatted-value="getLabel(
            transformerInfo.communicatingOilOltcTransformer,
            communicatingOilOptions,
          )"
        >
          <s-select
            v-model="transformerInfo.communicatingOilOltcTransformer"
            :disabled="shouldDisableCommunicatingOilOltcTransformer"
            :state="$v.transformerInfo.communicatingOilOltcTransformer.$invalid"
            :options="communicatingOilOptions"
          />
          <b-form-invalid-feedback>
            {{ getRequiredValueMessage('fields.asset.transformer.communicating-oil') }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="8"
        xl="8"
        cols="12"
      >
        <label>{{ $t('fields.asset.transformer.minimum-chain-to-determine-on-state') }}</label>
        <s-input-label-edit
          v-model="transformerInfo.minimalChainStatusOn"
          :formatted-value="formatNumberValue(transformerInfo.minimalChainStatusOn, 0)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.minimalChainStatusOn"
            :validation="$v.transformerInfo.minimalChainStatusOn"
            :invalid="$v.transformerInfo.minimalChainStatusOn.$invalid"
            :numeric-options="configureNumericOptionsPrecision(0)"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('minimalChainStatusOn', 0) }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="4"
        xl="4"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.life-expectancy') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          v-model="transformerInfo.lifespan"
          :formatted-value="formatNumberValue(transformerInfo.lifespan, 0)"
          :editable="editMode"
        >
          <s-number-input
            class="text-right"
            v-model="transformerInfo.lifespan"
            :validation="$v.transformerInfo.lifespan"
            :invalid="$v.transformerInfo.lifespan.$invalid"
            :numeric-options="configureNumericOptionsPrecision(0)"
          />
          <b-form-invalid-feedback>
            {{ getRangeValueMessage('lifespan', 0) }}
          </b-form-invalid-feedback>
        </s-input-label-edit>
      </b-col>
      <b-col
        class="mt-3"
        lg="8"
        xl="8"
        cols="12"
      >
        <label>
          {{ $t('fields.asset.transformer.ambient-measurement-temperature') }}
          <span class="pr-2">
            <s-icon
              name="hint"
              v-b-tooltip.hover.right
              :title="$t('asset-information.asset-info.engineering-modules-information')"
            />
          </span>
        </label>
        <s-input-label-edit
          :editable="editMode"
          :formatted-value="getLabel(
            transformerInfo.variableId,
            temperatureVariablesHandler,
          )"
          v-model="transformerInfo.variableId"
        >
          <s-select
            v-model="transformerInfo.variableId"
            :disabled="saving"
            :options="temperatureVariablesHandler"
            @change="variableIdChanged"
          />
        </s-input-label-edit>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        lg="8"
        xl="8"
        cols="12"
        class="lastReadingInfo"
      >
        <span class="lastReadingInfo">
          <label class="m-0">
            {{ $t('asset-information.asset-info.last-reading.last-returned-value') }}
          </label>
          {{ lastReadingInfo.readingValue }}{{ lastReadingInfo.measuringUnit }}
        </span>
        <span class="dateTimeSpan">
          <label class="m-0">
            {{ $t('asset-information.asset-info.last-reading.date') }}
          </label>
          {{ lastReadingInfo.readingDateTime }}
        </span>
      </b-col>
    </b-row>
    <b-row v-if="editMode">
      <b-col
        lg="8"
        xl="8"
        cols="12"
      >
        <b-alert
          class="mb-0 mt-2 py-1"
          variant="warning"
          :show="showTemperatureVariablesInfo"
        >
          <span>
            <label class="m-0">
              {{ $t('general.attention') }}!
            </label>
            {{ $t('asset-information.asset-info.dont-have-temperature-variables') }}
          </span>
        </b-alert>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        class="mt-3"
        cols="12"
      >
        <label>{{ $t('fields.asset.maintenance-history') }}</label>
        <s-input-label-edit
          :editable="editMode"
          v-model.trim="transformerInfo.maintenanceHistory"
          input-type="textarea"
          :input-options="{
            disabled: saving,
            maxLength: 2000,
          }"
        />
      </b-col>
      <b-col
        class="mt-3"
        cols="12"
      >
        <label>{{ $t('fields.general.observations') }}</label>
        <s-input-label-edit
          :editable="editMode"
          v-model.trim="transformerInfo.observation"
          input-type="textarea"
          :input-options="{
            disabled: saving,
            maxLength: 2000,
          }"
        />
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import { between, required, requiredIf } from 'vuelidate/lib/validators';
import {
  TransformerType,
  TensionRegulation,
  ElevationClass,
  InsulationType,
  OilType,
  TransformerApplication,
} from 'sigma-mappings';

const rangeRn4TensionPotency = { min: 0.01, max: 10000 };
const rangeRn4Chain = { min: 0.01, max: 15000 };
const rangeRn8 = { min: 1, max: 5000 };
const rangeLifeSpan = { min: 0, max: 100 };
const rangeRn11 = { min: 1, max: 1000000 };

const rangeValidations = {
  nominalPotency: rangeRn4TensionPotency,
  superiorTension: rangeRn4TensionPotency,
  inferiorTension: rangeRn4TensionPotency,
  tertiaryTension: rangeRn4TensionPotency,
  superiorNominalChain: rangeRn4Chain,
  inferiorNominalChain: rangeRn4Chain,
  tertiaryNominalChain: rangeRn4Chain,
  lifespan: rangeLifeSpan,
  minimalChainStatusOn: rangeRn8,
  oilVolume: rangeRn11,
  reservoirMass: rangeRn11,
  activeSideMass: rangeRn11,
  totalWeight: rangeRn11,
  oilKiloMass: rangeRn11,
};

const validationsToMerge = {
  transformerInfo: {
    transformerApplication: {
      required,
    },
    monitoredWindingAmount: {
      required,
    },
  },
};

Object.keys(rangeValidations).forEach((fieldName) => {
  validationsToMerge.transformerInfo[fieldName] = {
    between: between(rangeValidations[fieldName].min, rangeValidations[fieldName].max),
  };
});

export default {
  name: 'TransformerForm',
  props: {
    temperatureVariables: {
      type: Array,
      default: () => [],
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    transformerInfo: {
      type: Object,
    },
    lastReadingInfo: {
      type: Object,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    showTemperatureVariablesInfo() {
      /**
       * computed repassada ao b-panel.show:
       * Precisa ser boolean, caso for number, funcionará como timer.
       * caso o combo tenha apenas a opção de "selecione" então o length será = 1
       */
      return this.temperatureVariablesHandler.length <= 1;
    },
    temperatureVariablesHandler() {
      return [
        this.nullCase,
        ...this.temperatureVariables.map(item => ({
          text: this.variablesNameFormatter(item),
          value: item.id,
        })),
      ];
    },
    invalidForm() {
      return this.$v.$invalid;
    },
    nullCase() {
      return {
        text: this.$t('general.select'),
        value: null,
      };
    },
    typeOptions() {
      return [
        this.nullCase,
        {
          text: this.$t('enums.transformer-type.autotransformer'),
          value: TransformerType.AUTOTRANSFORMER,
        },
        {
          text: this.$t('enums.transformer-type.reactor'),
          value: TransformerType.REACTOR,
        },
        {
          text: this.$t('enums.transformer-type.transformer'),
          value: TransformerType.TRANSFORMER,
        },
        {
          text: this.$t('enums.transformer-type.grounding-transformer'),
          value: TransformerType.GROUNDING_TRANSFORMER,
        },
      ];
    },
    transformerApplicationOptions() {
      return [
        {
          text: this.$t('enums.transformer-application.transformer-potency'),
          value: TransformerApplication.POTENCY,
        },
        {
          text: this.$t('enums.transformer-application.transformer-distribution'),
          value: TransformerApplication.DISTRIBUTION,
        },
        {
          text: this.$t('enums.transformer-application.transformer-oven'),
          value: TransformerApplication.OVEN,
        },
      ];
    },
    windingCountOptions() {
      return [
        this.nullCase,
        {
          text: this.$t('general.yes'),
          value: true,
        },
        {
          text: this.$t('general.no'),
          value: false,
        },
      ];
    },
    monitoredWindingOptions() {
      const baseValues = [this.nullCase, '1', '2'];
      return this.transformerInfo.tertiaryWinding !== false ? [...baseValues, '3'] : baseValues;
    },
    tensionRegulationOptions() {
      return [
        this.nullCase,
        {
          text: this.$t('enums.tension-regulation.does-not-have-tension-regulation'),
          value: TensionRegulation.DOES_NOT_HAVE_TENSION_REGULATION,
        },
        {
          text: this.$t('enums.tension-regulation.has-no-load-tap-changer'),
          value: TensionRegulation.NO_LOAD_TAP_CHANGER,
        },
        {
          text: this.$t('enums.tension-regulation.has-on-load-tap-changer'),
          value: TensionRegulation.ON_LOAD_TAP_CHANGER,
        },
      ];
    },
    elevationClassOptions() {
      return [
        this.nullCase,
        {
          text: '55',
          value: ElevationClass.VALUE_55,
        },
        {
          text: '65',
          value: ElevationClass.VALUE_65,
        },
        {
          text: '95',
          value: ElevationClass.VALUE_95,
        },
      ];
    },
    insulatingPaperTypeOptions() {
      return [
        this.nullCase,
        {
          text: this.$t('enums.insulating-paper-type.thermostabilized'),
          value: InsulationType.THERMOSTABILIZED,
        },
        {
          text: this.$t('enums.insulating-paper-type.not-thermostabilized'),
          value: InsulationType.NOT_THERMOSTABILIZED,
        },
      ];
    },
    oilTypeOptions() {
      return [
        this.nullCase,
        {
          text: this.$t('enums.oil-type.mineral'),
          value: OilType.MINERAL,
        },
        {
          text: this.$t('enums.oil-type.silicone'),
          value: OilType.SILICONE,
        },
        {
          text: this.$t('enums.oil-type.vegetal'),
          value: OilType.VEGETAL,
        },
      ];
    },
    communicatingOilOptions() {
      return [
        {
          text: this.$t('general.no'),
          value: false,
        },
        {
          text: this.$t('general.yes'),
          value: true,
        },
      ];
    },
    options() {
      return {
        insulationType: {
          disabled: this.saving || this.transformerInfo.elevationClass === ElevationClass.VALUE_65
            || this.transformerInfo.elevationClass === ElevationClass.VALUE_95,
        },
        superiorTension: {
          rangeRn4TensionPotency,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.transformerInfo.superiorTension.$invalid),
          disabled: this.saving,
        },
        superiorNominalChain: {
          rangeRn4Chain,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.transformerInfo.superiorNominalChain.$invalid),
          disabled: this.saving,
        },
        lifespan: {
          rangeLifeSpan,
          maxLength: 3,
          state: this.$bsStateValidator(this.$v.transformerInfo.lifespan.$invalid),
          disabled: this.saving,
        },
        minimalChainStatusOn: {
          rangeRn8,
          maxLength: 3,
          state: this.$bsStateValidator(this.$v.transformerInfo.minimalChainStatusOn.$invalid),
          disabled: this.saving,
        },
        oilKiloMass: {
          rangeRn11,
          maxLength: 10,
          state: this.$bsStateValidator(this.$v.transformerInfo.oilKiloMass.$invalid),
          disabled: this.saving,
        },
        oilVolume: {
          rangeRn11,
          maxLength: 10,
          state: this.$bsStateValidator(this.$v.transformerInfo.oilVolume.$invalid),
          disabled: this.saving,
        },
        reservoirMass: {
          rangeRn11,
          maxLength: 10,
          state: this.$bsStateValidator(this.$v.transformerInfo.reservoirMass.$invalid),
          disabled: this.saving,
        },
        activeSideMass: {
          rangeRn11,
          maxLength: 10,
          state: this.$bsStateValidator(this.$v.transformerInfo.activeSideMass.$invalid),
          disabled: this.saving,
        },
        totalWeight: {
          rangeRn11,
          maxLength: 10,
          state: this.$bsStateValidator(this.$v.transformerInfo.totalWeight.$invalid),
          disabled: this.saving,
        },
        tertiaryNominalChain: {
          rangeRn4Chain,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.transformerInfo.tertiaryNominalChain.$invalid),
          disabled: this.disableTertiary || this.transformerIsReactorOrGrounding,
        },
        inferiorNominalChain: {
          rangeRn4Chain,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.transformerInfo.inferiorNominalChain.$invalid),
          disabled: this.transformerIsReactorOrGrounding,
        },
        tertiaryTension: {
          rangeRn4TensionPotency,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.transformerInfo.tertiaryTension.$invalid),
          disabled: this.disableTertiary || this.transformerIsReactorOrGrounding,
        },
        inferiorTension: {
          rangeRn4TensionPotency,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.transformerInfo.inferiorTension.$invalid),
          disabled: this.transformerIsReactorOrGrounding,
        },
        nominalPotency: {
          rangeRn4TensionPotency,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.transformerInfo.nominalPotency.$invalid),
          disabled: this.saving,
        },
      };
    },
    transformerIsReactorOrGrounding() {
      return this.transformerInfo.type === TransformerType.REACTOR
        || this.transformerInfo.type === TransformerType.GROUNDING_TRANSFORMER
        || this.saving;
    },
    disableTertiary() {
      return this.transformerIsReactorOrGrounding || this.transformerInfo.tertiaryWinding === false;
    },
    isSuperiorTensionLowerThanInferiorTension() {
      const { superiorTension, inferiorTension } = this.$v.transformerInfo;
      return inferiorTension.$model > superiorTension.$model;
    },
    shouldDisableCommunicatingOilOltcTransformer() {
      const { tensionRegulation } = this.transformerInfo;
      return this.saving || (
        tensionRegulation !== TensionRegulation.NO_LOAD_TAP_CHANGER
        && tensionRegulation !== TensionRegulation.ON_LOAD_TAP_CHANGER
      );
    },
    isTotalWeightLowerThanSumOfActiveSideMassAndReservoirMass() {
      const { activeSideMass, reservoirMass, totalWeight } = this.$v.transformerInfo;
      if (!activeSideMass.$model || !reservoirMass.$model || !totalWeight.$model) return false;
      return totalWeight.$model < activeSideMass.$model + reservoirMass.$model;
    },
  },
  created() {
    this.updateFormStatus(this.invalidForm);
  },
  validations() {
    const upperOrEqualInferiorTension = () => !this
      .isSuperiorTensionLowerThanInferiorTension;

    validationsToMerge.transformerInfo.communicatingOilOltcTransformer = {
      required: requiredIf(() => !this.shouldDisableCommunicatingOilOltcTransformer),
    };

    const greaterThanMassSum = () => !this
      .isTotalWeightLowerThanSumOfActiveSideMassAndReservoirMass;

    validationsToMerge.transformerInfo.superiorTension = {
      ...validationsToMerge.transformerInfo.superiorTension,
      upperOrEqualInferiorTension,
    };
    validationsToMerge.transformerInfo.totalWeight = {
      ...validationsToMerge.transformerInfo.totalWeight,
      greaterThanMassSum,
    };

    return validationsToMerge;
  },
  methods: {
    onChangeType(type) {
      if (type === TransformerType.GROUNDING_TRANSFORMER || type === TransformerType.REACTOR) {
        this.$set(this.transformerInfo, 'inferiorTension', null);
        this.$set(this.transformerInfo, 'tertiaryTension', null);
        this.$set(this.transformerInfo, 'tertiaryNominalChain', null);
        this.$set(this.transformerInfo, 'inferiorNominalChain', null);
        this.$set(this.transformerInfo, 'tertiaryWinding', false);
      }
    },
    updateFormStatus(value) {
      this.$emit('form-status-update', value);
    },
    configureNumericOptionsPrecision(decimalPlaces = 0) {
      return {
        decimalPlaces,
      };
    },
    getTensionMessage() {
      return this.$t('messages.MA038.message', {
        NomeDoCampo: this.$t('fields.asset.transformer.higher-voltage'),
        FaixaInicial: this.$t('fields.asset.transformer.lower-voltage'),
      });
    },
    getSuperiorTensionValidation(fieldName, precision = 2) {
      if (this.isSuperiorTensionLowerThanInferiorTension === true) {
        return this.getTensionMessage();
      }
      return this.getRangeValueMessage(fieldName, precision);
    },
    getTotalWeightMessage() {
      return this.$t('messages.MA106.message', {
        totalWeightField: this.$t('fields.asset.transformer.total-weight'),
        activeSideMassField: this.$t('fields.asset.transformer.mass-active-part'),
        reservoirMassField: this.$t('fields.asset.transformer.tank-mass-and-accessories'),
      });
    },
    getTotalWeightValidation(fieldName, precision = 1) {
      if (this.isTotalWeightLowerThanSumOfActiveSideMassAndReservoirMass === true) {
        return this.getTotalWeightMessage();
      }
      return this.getRangeValueMessage(fieldName, precision);
    },
    getSuperiorNominalChainValidation(fieldName, precision = 2) {
      return this.getRangeValueMessage(fieldName, precision);
    },
    getRangeValueMessage(fieldName, precision = 2) {
      this.emitValidationState();
      const numberFormat = {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(rangeValidations[fieldName].min, numberFormat),
        maxValue: this.$n(rangeValidations[fieldName].max, numberFormat),
      });
    },
    getRequiredValueMessage(fieldPath) {
      this.emitValidationState();

      return this.$t('messages.required.message', {
        field: this.$t(fieldPath),
      });
    },
    emitValidationState() {
      this.$emit('validate', !this.$v.$invalid);
    },
    getLabel(label, options) {
      if (label == null || label === '' || label === 'undefined') {
        return null;
      }

      const labelProxy = options.find(({ value }) => value === label);
      return labelProxy ? labelProxy.text : label;
    },
    formatNumberValue(value, decimais = 0) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: decimais,
        maximumFractionDigits: decimais,
      });
    },
    tertiaryWindingChange(newValue) {
      const windingAmount = (this.transformerInfo.monitoredWindingAmount || {}).toString();
      if (newValue === false && windingAmount === '3') {
        this.$set(this.transformerInfo, 'monitoredWindingAmount', null);
      }
      if (newValue === false) {
        this.$set(this.transformerInfo, 'tertiaryTension', null);
        this.$set(this.transformerInfo, 'tertiaryNominalChain', null);
      }
    },
    monitoredWindingAmountChange(newValue) {
      this.$set(this.transformerInfo, 'monitoredWindingAmount', newValue);
      if (newValue === '3' && this.transformerInfo.tertiaryWinding === false) {
        this.$set(this.transformerInfo, 'tertiaryWinding', null);
      }
    },
    elevationClassChanged(value) {
      if (value === ElevationClass.VALUE_65 || value === ElevationClass.VALUE_95) {
        this.$set(this.transformerInfo, 'insulationType', InsulationType.THERMOSTABILIZED);
      }
    },
    rangeProxyFormatter(value, min, max) {
      const minHandler = value < min ? null : value;
      return value >= max ? max : minHandler;
    },
    numberFormatter(min, max, maxDecimals) {
      return (value) => {
        const splitValue = value.split('.');
        const decimals = value.includes('.') ? splitValue.pop() : null;
        const valueProxy = this.rangeProxyFormatter(
          decimals ? value : splitValue[0],
          min,
          max,
        );
        return decimals && decimals.length > maxDecimals
          ? Number(`${splitValue[0]}.${decimals.replace(/\w$/g, '')}`)
          : valueProxy;
      };
    },
    variablesNameFormatter({
      assetName,
      moduleName,
      name,
    }) {
      return `${assetName} | ${moduleName} - ${name}`;
    },
    onChangeTensionRegulation() {
      if (this.shouldDisableCommunicatingOilOltcTransformer) {
        this.transformerInfo.communicatingOilOltcTransformer = false;
        return;
      }

      this.$nextTick(() => {
        this.$v.transformerInfo.communicatingOilOltcTransformer.$touch();
      });
    },
    variableIdChanged(value) {
      if (value) {
        this.$emit('variable-id-changed', value);
        return;
      }
      this.clearLastReadingInfoValues();
    },
    clearLastReadingInfoValues() {
      Object.keys(this.lastReadingInfo).forEach(key => delete this.lastReadingInfo[key]);
    },
  },
  watch: {
    invalidForm(value) {
      this.updateFormStatus(value);
    },
  },
};
</script>

<style lang="sass" scoped>
  .lastReadingInfo
    color: var(--color-primary)
  .dateTimeSpan
    padding-left: 30px
</style>
